import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.6, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const channels = [
  { icon: Mail, title: "Email us", value: "support@vsense.com", sub: "We reply within 24 hours." },
  { icon: MessageCircle, title: "Live chat", value: "Chat with us", sub: "Mon–Fri, 9am–6pm PT." },
  { icon: Phone, title: "Call us", value: "+1 (800) 555-0142", sub: "Toll-free support line." },
  { icon: MapPin, title: "Visit", value: "San Francisco, CA", sub: "By appointment only." },
];

const faqs = [
  { q: "Is the reservation really free?", a: "Yes — completely free with no credit card required. Reserving simply holds your spot in line and locks in your 40% early-reservation discount. You'll only pay when preorders officially open and you confirm your order." },
  { q: "How does the 40% discount code work?", a: "Every reservation generates a unique discount code that can only be used once and is tied to you. Please don't share it. We'll send your code via text and email the moment preorders go live." },
  { q: "What's the difference between Vsense Kids and Vsense Adult?", a: "Both share the same core safety features — live location, two-way voice, streaming and emergency SOS. Vsense Kids uses a smaller, extra-comfortable frame sized for children, while Vsense Adult offers a larger fit and adult-focused styling." },
  { q: "Do the glasses need a phone to work?", a: "The glasses pair with the free Vsense guardian app on a paired phone or the wearer's device for connectivity. Core features like GPS, streaming and voice run through the app so guardians can stay connected from anywhere." },
  { q: "Can I add a prescription?", a: "Yes. Choose the Prescription-Ready lens option when customizing, and we'll guide you through adding your prescription details after preorders open." },
  { q: "When will the glasses ship?", a: "We'll announce exact ship dates as preorders open. Everyone with a reservation gets priority access and first-batch shipping ahead of the general public." },
  { q: "What about my data and privacy?", a: "Your family's data is encrypted and never sold. You control who has access, and you can delete your data at any time. Read our Privacy Policy for the full details." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const field =
    "w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 transition-colors";

  const submit = (e) => {
    e.preventDefault();
    toast.success("Message sent — we'll get back to you within 24 hours.");
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-14 md:pb-20 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">We're here to help</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-zinc-900 leading-[1.05]">
              Get in touch.
            </h1>
            <p className="mt-5 text-base md:text-lg text-zinc-500 max-w-xl">
              Questions about reservations, the app, or your glasses? Our team would love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Channels */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {channels.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05}>
              <div data-testid={`contact-channel-${i}`} className="h-full rounded-2xl border border-zinc-200 p-6 hover:border-zinc-900 transition-colors duration-200">
                <div className="w-11 h-11 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
                  <c.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 text-sm text-zinc-400">{c.title}</h3>
                <p className="mt-0.5 font-semibold text-zinc-900">{c.value}</p>
                <p className="mt-1 text-xs text-zinc-500">{c.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Form + FAQ */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Form */}
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-zinc-900">
              Send us a message
            </h2>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="contact-name" className={field} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input required type="email" data-testid="contact-email" className={field} placeholder="Email" value={form.email} onChange={set("email")} />
              </div>
              <input required data-testid="contact-subject" className={field} placeholder="Subject" value={form.subject} onChange={set("subject")} />
              <textarea required rows={6} data-testid="contact-message" className={field} placeholder="How can we help?" value={form.message} onChange={set("message")} />
              <button
                type="submit"
                data-testid="contact-submit"
                className="w-full sm:w-auto bg-zinc-900 text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
              >
                Send message
              </button>
            </form>
          </Reveal>

          {/* FAQ */}
          <Reveal delay={0.1}>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-zinc-900">
              Frequently asked
            </h2>
            <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-sm md:text-base font-medium text-zinc-900">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-500 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
